<template>
  <div>
    <loading-panel :loading="loading"></loading-panel>

    <card>
      <!-- Header: affiliates/tiers tab rail with the create action trailing on the rail -->
      <div class="row mb-3">
        <div class="col">
          <tab-rail :tabs="categories" :value="activeTabIndex" @input="switchTab">
            <template v-if="canUpdate" #actions>
              <base-button v-if="view === 'affiliates'" @click="openCreateAffiliate" type="primary" size="sm" :disabled="activeTiers.length === 0">
                <i class="tim-icons el-icon-plus"></i><span class="d-none d-sm-inline ml-1">Create affiliate</span>
              </base-button>
              <base-button v-if="view === 'tiers'" @click="openCreateTier" type="primary" size="sm">
                <i class="tim-icons el-icon-plus"></i><span class="d-none d-sm-inline ml-1">Create tier</span>
              </base-button>
            </template>
          </tab-rail>
        </div>
      </div>

      <!-- Affiliates empty state -->
      <div v-if="view === 'affiliates' && affiliates.length === 0 && !loading" class="af-empty-state">
        <img src="/img/pandas/marketing.png" alt="No affiliates" class="empty-state-img" />
        <h4 class="empty-state-text">No affiliates yet</h4>
        <p class="af-empty-state__hint" v-if="activeTiers.length === 0">
          Start with a commission tier on the tiers tab. Then create an affiliate
          to get a shareable booking link and QR code
        </p>
        <p class="af-empty-state__hint" v-else>
          Create an affiliate to get a shareable booking link and QR code.
          Sales made through the link earn commission automatically
        </p>
        <base-button v-if="canUpdate && activeTiers.length === 0" type="primary" size="sm" class="mt-3" @click="switchTab(1)">
          Go to tiers
        </base-button>
      </div>

      <!-- Affiliates list -->
      <div v-if="view === 'affiliates' && affiliates.length > 0" class="af-list mt-3">
        <div
          v-for="affiliate in sortedAffiliates"
          :key="'AF' + affiliate.id"
          class="af-card"
          :class="{ 'af-card--archived': affiliate.status === 'archived' }"
          @click="openDrawer(affiliate)"
        >
          <div class="af-card__body">
            <div class="af-card__info">
              <div class="af-card__label text-white">{{ affiliate.name }}</div>

              <div class="af-card__meta">
                <span class="af-card__meta-detail">{{ typeLabel(affiliate.type) }}</span>
                <span class="af-card__meta-detail">{{ affiliate.tier_name || 'No tier' }}</span>
                <span class="af-card__meta-detail">{{ affiliate.email }}</span>
              </div>

              <div class="af-card__usage">
                <span class="af-card__usage-label">Source</span>
                <span class="af-card__usage-text">{{ affiliate.ref_code }}</span>
                <span class="af-card__usage-label">Owed</span>
                <span class="af-card__usage-text">{{ formatMoney(owedCents(affiliate) / 100) }}</span>
                <template v-if="(affiliate.promo_codes || []).length > 0">
                  <span class="af-card__usage-label">Promo codes</span>
                  <span class="af-card__usage-text">{{ affiliate.promo_codes.map(p => p.name).join(', ') }}</span>
                </template>
              </div>
            </div>

            <!-- Right: badges + actions (click-stopped so the row click cannot swallow the menu) -->
            <div class="af-card__actions" @click.stop>
              <span v-if="affiliate.status === 'archived'" class="af-badge af-badge--archived">Archived</span>

              <el-dropdown @click.native.stop>
                <base-button simple type="primary" link size="sm">
                  <i class="tim-icons el-icon-more"></i>
                </base-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <a class="dropdown-item mt-1 mb-2" href="#" @click.prevent="openDrawer(affiliate)">
                      <i class="tim-icons icon-chart-bar-32 mr-2"></i> View performance
                    </a>
                    <a class="dropdown-item mt-1 mb-2" href="#" @click.prevent="copyLink(affiliate)">
                      <i class="tim-icons icon-link-72 mr-2"></i> Copy booking link
                    </a>
                    <a class="dropdown-item mt-1 mb-2" href="#" @click.prevent="openQr(affiliate)">
                      <i class="tim-icons icon-camera-18 mr-2"></i> QR code
                    </a>
                    <a v-if="canUpdate" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="openEditAffiliate(affiliate)">
                      <i class="tim-icons icon-pencil mr-2"></i> Edit affiliate
                    </a>
                    <a v-if="canUpdate && affiliate.status === 'active'" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="archiveAffiliatePrompt(affiliate)">
                      <i class="tim-icons icon-app mr-2"></i> Archive
                    </a>
                    <a v-if="canUpdate && affiliate.status === 'archived'" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="setAffiliateArchived(affiliate, false)">
                      <i class="tim-icons icon-refresh-02 mr-2"></i> Restore
                    </a>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </div>
        </div>
      </div>

      <!-- Tiers empty state -->
      <div v-if="view === 'tiers' && tiers.length === 0 && !loading" class="af-empty-state">
        <img src="/img/pandas/marketing.png" alt="No tiers" class="empty-state-img" />
        <h4 class="empty-state-text">No tiers yet</h4>
        <p class="af-empty-state__hint">
          Tiers define the commission an affiliate earns per attributed sale,
          a percent of the sale, a flat amount, or both
        </p>
      </div>

      <!-- Tiers list: active tiers drag to reorder, archived pinned below -->
      <div v-if="view === 'tiers' && tiers.length > 0" class="mt-3">
        <draggable
          v-model="draggableTiers"
          class="af-list"
          handle=".drag-handle"
          :animation="200"
          :disabled="!canUpdate"
          ghost-class="af-card--ghost"
          @end="reorderTiers"
        >
          <div
            v-for="tier in draggableTiers"
            :key="'AT' + tier.id"
            class="af-card"
            @click="canUpdate && openEditTier(tier)"
          >
            <div class="af-card__body">
              <i v-if="canUpdate" style="cursor: move" class="tim-icons el-icon-rank drag-handle" @click.stop></i>
              <div class="af-card__info">
                <div class="af-card__label text-white">{{ tier.name }}</div>

                <div class="af-card__meta">
                  <span class="af-card__meta-detail">{{ tierCommissionText(tier) }}</span>
                  <span class="af-card__meta-detail">{{ tier.commission_base === 'subtotal' ? 'Of subtotal after discounts' : 'Of total without tips' }}</span>
                </div>
              </div>

              <div class="af-card__actions" @click.stop>
                <el-dropdown v-if="canUpdate" @click.native.stop>
                  <base-button simple type="primary" link size="sm">
                    <i class="tim-icons el-icon-more"></i>
                  </base-button>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <a class="dropdown-item mt-1 mb-2" href="#" @click.prevent="openEditTier(tier)">
                        <i class="tim-icons icon-pencil mr-2"></i> Edit tier
                      </a>
                      <a class="dropdown-item mt-1 mb-2" href="#" @click.prevent="setTierArchived(tier, true)">
                        <i class="tim-icons icon-app mr-2"></i> Archive
                      </a>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
            </div>
          </div>
        </draggable>

        <div class="af-list mt-2">
          <div
            v-for="tier in archivedTiers"
            :key="'AT' + tier.id"
            class="af-card af-card--archived"
            @click="canUpdate && openEditTier(tier)"
          >
            <div class="af-card__body">
              <div class="af-card__info">
                <div class="af-card__label text-white">{{ tier.name }}</div>

                <div class="af-card__meta">
                  <span class="af-card__meta-detail">{{ tierCommissionText(tier) }}</span>
                  <span class="af-card__meta-detail">{{ tier.commission_base === 'subtotal' ? 'Of subtotal after discounts' : 'Of total without tips' }}</span>
                </div>
              </div>

              <div class="af-card__actions" @click.stop>
                <span class="af-badge af-badge--archived">Archived</span>

                <el-dropdown v-if="canUpdate" @click.native.stop>
                  <base-button simple type="primary" link size="sm">
                    <i class="tim-icons el-icon-more"></i>
                  </base-button>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <a class="dropdown-item mt-1 mb-2" href="#" @click.prevent="openEditTier(tier)">
                        <i class="tim-icons icon-pencil mr-2"></i> Edit tier
                      </a>
                      <a class="dropdown-item mt-1 mb-2" href="#" @click.prevent="setTierArchived(tier, false)">
                        <i class="tim-icons icon-refresh-02 mr-2"></i> Restore
                      </a>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
            </div>
          </div>
        </div>
      </div>
    </card>

    <affiliate-form-drawer
      :show="affiliateDrawerOpen"
      :affiliate="editingAffiliate"
      :tiers="tiers"
      :promos="promos"
      @close="affiliateDrawerOpen = false"
      @saved="loadAll(false)"
    />

    <tier-form-drawer
      :show="tierDrawerOpen"
      :tier="editingTier"
      :next-sort-order="tiers.length"
      @close="tierDrawerOpen = false"
      @saved="loadAll(false)"
    />

    <!-- QR drawer -->
    <base-drawer :show="qrOpen" :title="'QR code for ' + qr.name" width="400px" @close="qrOpen = false">
      <div ref="qrContainer" class="text-center py-4 qr-canvas-wrap">
        <qrcode-vue :value="qr.value" :size="qr.size" level="H" />
      </div>
      <small class="d-block text-center qr-link-text">{{ qr.value }}</small>
      <template #footer>
        <div class="footer-actions">
          <base-button type="secondary" size="sm" @click="qrOpen = false">Close</base-button>
          <base-button type="primary" size="sm" @click="downloadQr">
            <i class="tim-icons icon-cloud-download-93 mr-1"></i> Download
          </base-button>
        </div>
      </template>
    </base-drawer>

    <base-alert-dialog
      :show="!!archivePrompt"
      :title="archivePrompt ? `Archive ${archivePrompt.name}?` : ''"
      message="Archived affiliates stop earning commission. Their history and any amount owed stay on record."
      variant="warning"
      show-cancel
      confirm-text="Archive"
      cancel-text="Keep it"
      confirm-variant="danger"
      @confirm="confirmArchive"
      @cancel="archivePrompt = null"
    />

    <affiliate-detail-drawer
      :show="drawerOpen"
      :affiliate="drawerAffiliate"
      :link="drawerAffiliate ? affiliateLink(drawerAffiliate) : ''"
      :can-update="canUpdate"
      :tiers="tiers"
      @close="drawerOpen = false"
      @changed="loadAll(false)"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import axios from 'axios'
import TabRail from '@/components/TabRail.vue'
import { Auth } from 'aws-amplify'
import { API_LOCATION, BOOKING_URL } from '@/api-config'
import { formatCurrency, getGroupSettings } from '@/util/currencyFormat'
import { buildAffiliateLink } from '@/util/affiliateLink'
import QrcodeVue from 'qrcode.vue'
import draggable from 'vuedraggable'
import AffiliateDetailDrawer from './AffiliateDetailDrawer.vue'
import AffiliateFormDrawer from './AffiliateFormDrawer.vue'
import TierFormDrawer from './TierFormDrawer.vue'

const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const group = JSON.parse(localStorage.getItem('group') || '{}')
const companyGroupId = group.id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}`

const loading = ref(true)
const tiers = ref<any[]>([])
const affiliates = ref<any[]>([])
const promos = ref<any[]>([])

const view = ref('affiliates')
const activeTabIndex = ref(0)
const categories = [
  { name: 'Affiliates', shortName: 'Affiliates' },
  { name: 'Tiers', shortName: 'Tiers' }
]

const affiliateDrawerOpen = ref(false)
const tierDrawerOpen = ref(false)
const qrOpen = ref(false)
const editingAffiliate = ref<any>(null)
const editingTier = ref<any>(null)
const qr = ref({ value: '', size: 240, name: '' })
const qrContainer = ref<any>(null)

const drawerOpen = ref(false)
const drawerAffiliate = ref<any>(null)

const typeOptions = [
  { value: 'influencer', label: 'Influencer' },
  { value: 'local_business', label: 'Local business' },
  { value: 'player', label: 'Player' }
]

const activeTiers = computed(() => tiers.value.filter((t) => t.is_archived === 0))

// Active rows first, archived greyed at the bottom (same reading order as
// the campaigns and passes lists).
const sortedAffiliates = computed(() => {
  const active = affiliates.value.filter((a) => a.status === 'active')
  const archived = affiliates.value.filter((a) => a.status === 'archived')
  return [...active, ...archived]
})

// Writable list for drag-to-reorder (draggable needs v-model, not a computed).
const draggableTiers = ref<any[]>([])
const archivedTiers = computed(() => tiers.value.filter((t) => t.is_archived === 1))

// TEMPORARY: ungated for local testing until the affiliates permission seed
// runs (phase 4.7). Restore hasPermissions('affiliates_update') before release.
const canUpdate = computed(() => true || hasPermissions('affiliates_update'))

function hasPermissions(scope: string) {
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
  return permissions.some((element: string) => element === scope)
}

function switchTab(index: number) {
  activeTabIndex.value = index
  view.value = index === 1 ? 'tiers' : 'affiliates'
}

function typeLabel(type: string) {
  const match = typeOptions.find((t) => t.value === type)
  return match ? match.label : type
}

function formatMoney(value: any) {
  return formatCurrency(parseFloat(value) || 0, {
    currency: group.currency,
    currencyFormat: group.currency_format,
    numberFormat: group.number_format
  })
}

// House rule: money math in integer cents via exponential notation.
function toCents(x: any) {
  const n = parseFloat(x)
  if (Number.isNaN(n)) return 0
  if (Math.abs(n) < 0.005) return 0
  return Math.round(Number(n + 'e2'))
}

// reversed sums are stored negative; owed = earned + reversed - paid
function owedCents(affiliate: any) {
  return toCents(affiliate.earned) + toCents(affiliate.reversed) - toCents(affiliate.paid)
}

function tierCommissionText(tier: any) {
  const parts = []
  const pct = parseFloat(tier.commission_percent)
  if (!Number.isNaN(pct) && tier.commission_percent != null) parts.push(`${pct}%`)
  const flat = parseFloat(tier.commission_flat)
  if (!Number.isNaN(flat) && tier.commission_flat != null) parts.push(formatMoney(flat))
  return parts.join(' + ') || 'No commission'
}

function affiliateLink(affiliate: any) {
  return buildAffiliateLink(BOOKING_URL, group.code, affiliate)
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

// House convention: new lambdas return JSON errors; older ones return strings.
function errMessage(err: any, fallback: string) {
  const data = err?.response?.data
  if (data) {
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data
      if (parsed && parsed.error) return parsed
    } catch (e) { /* not JSON */ }
    if (typeof data === 'string' && data.length > 0 && data.length < 200) return { error: data }
  }
  return { error: fallback }
}

function notifyError(message: string) {
  proxy.$notify({ type: 'danger', message, icon: 'tim-icons icon-alert-circle-exc' })
}

function notifySuccess(message: string) {
  proxy.$notify({ type: 'success', message, icon: 'tim-icons icon-check-2' })
}

async function loadAll(showLoader = true) {
  if (showLoader) loading.value = true
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}/affiliates`, config)
    tiers.value = response.data.tiers || []
    affiliates.value = response.data.affiliates || []
    draggableTiers.value = tiers.value.filter((t: any) => t.is_archived === 0)
  } catch (err: any) {
    if (err?.response?.status === 401 || err?.response?.status === 403) {
      proxy.$router.push('/forbidden')
      return
    }
    notifyError('Failed to load affiliates. Please try again.')
  } finally {
    loading.value = false
  }
}

async function loadPromos() {
  try {
    const config = await authConfig()
    const url = `${baseUrl}/promos?count=1000&offset=0&archived=0&name=&template_ids=`
    const response = await axios.get(url, config)
    promos.value = response.data.promos || []
  } catch (err: any) {
    // Promo linking is optional; the page still works without the list.
    promos.value = []
  }
}

function openCreateAffiliate() {
  editingAffiliate.value = null
  affiliateDrawerOpen.value = true
}

function openEditAffiliate(affiliate: any) {
  editingAffiliate.value = affiliate
  affiliateDrawerOpen.value = true
}

// Archive confirm lives in base-alert-dialog; the pending affiliate is the
// dialog's open state.
const archivePrompt = ref<any>(null)

function archiveAffiliatePrompt(affiliate: any) {
  archivePrompt.value = affiliate
}

function confirmArchive() {
  const affiliate = archivePrompt.value
  archivePrompt.value = null
  if (affiliate) setAffiliateArchived(affiliate, true)
}

async function setAffiliateArchived(affiliate: any, archived: boolean) {
  try {
    const config = await authConfig()
    await axios.put(`${baseUrl}/affiliates/${affiliate.id}`, { status: archived ? 'archived' : 'active' }, config)
    notifySuccess(archived ? 'Affiliate archived' : 'Affiliate restored')
    await loadAll(false)
  } catch (err: any) {
    notifyError(errMessage(err, 'Failed to update affiliate').error)
  }
}

function openCreateTier() {
  editingTier.value = null
  tierDrawerOpen.value = true
}

function openEditTier(tier: any) {
  editingTier.value = tier
  tierDrawerOpen.value = true
}

// Persist the dragged order: sort_order = index in the active list. Only
// tiers whose position changed get a PUT, sequentially.
async function reorderTiers() {
  try {
    const config = await authConfig()
    for (let i = 0; i < draggableTiers.value.length; i++) {
      const tier = draggableTiers.value[i]
      if (tier.sort_order !== i) {
        await axios.put(`${baseUrl}/affiliate-tiers/${tier.id}`, { sort_order: i }, config)
      }
    }
    await loadAll(false)
  } catch (err: any) {
    notifyError(errMessage(err, 'Failed to reorder tiers').error)
    await loadAll(false)
  }
}

async function setTierArchived(tier: any, archived: boolean) {
  try {
    const config = await authConfig()
    await axios.put(`${baseUrl}/affiliate-tiers/${tier.id}`, { is_archived: archived ? 1 : 0 }, config)
    notifySuccess(archived ? 'Tier archived' : 'Tier restored')
    await loadAll(false)
  } catch (err: any) {
    notifyError(errMessage(err, 'Failed to update tier').error)
  }
}

async function copyLink(affiliate: any) {
  try {
    await navigator.clipboard.writeText(affiliateLink(affiliate))
    notifySuccess('Booking link copied')
  } catch (e) {
    notifyError('Could not copy the link')
  }
}

function openQr(affiliate: any) {
  qr.value = { value: affiliateLink(affiliate), size: 240, name: affiliate.name }
  qrOpen.value = true
}

function downloadQr() {
  const canvas = qrContainer.value ? qrContainer.value.querySelector('canvas') : null
  if (!canvas) return
  const link = document.createElement('a')
  link.download = `affiliate-qr-${qr.value.name || 'code'}.png`
  link.href = canvas.toDataURL()
  link.click()
}

function openDrawer(affiliate: any) {
  drawerAffiliate.value = affiliate
  drawerOpen.value = true
}

onMounted(async () => {
  await loadAll()
  await loadPromos()
})
</script>

<script lang="ts">
// el-dropdown is not globally registered (see globalComponents.js); register
// locally like every other consumer. Unaliased imports only.
import { Dropdown, DropdownMenu } from 'element-ui'

export default {
  name: 'affiliates-page',
  components: {
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu
  }
}
</script>

<style scoped>
/* Row-card list, same language as the passes list (pl-card), campaigns list
   (cl-card), and PaymentPagesList (pp-card). Layout + theme-neutral tints only. */
.af-empty-state {
  text-align: center;
  padding: 0.5rem 1rem 2rem;
}
.af-empty-state__hint {
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  max-width: 480px;
  margin: 8px auto 0;
}

.af-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.af-card {
  background: rgba(128, 128, 128, 0.05);
  border-left: 3px solid #e14eca;
  border-radius: 0.375rem;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s ease;
}
.af-card:hover {
  background: rgba(128, 128, 128, 0.12);
}
.af-card--archived {
  opacity: 0.5;
  border-left-color: rgba(125, 125, 125, 0.4);
}
.af-card--ghost {
  opacity: 0.3;
}
.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
/* The affiliate URL is one unbroken string; without break-all it forces the
   QR drawer body to scroll horizontally. */
.qr-link-text {
  word-break: break-all;
  overflow-wrap: anywhere;
}
.qr-canvas-wrap {
  max-width: 100%;
  overflow: hidden;
}
.drag-handle {
  flex-shrink: 0;
  color: rgba(125, 125, 125, 0.8);
}
.af-card__body {
  display: flex;
  align-items: center;
  gap: 14px;
}

.af-card__info {
  flex: 1;
  min-width: 0;
}
.af-card__label {
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.af-card__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 3px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.75);
}
.af-card__meta-detail {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.af-card__meta-detail:not(:first-child)::before {
  content: '\00b7';
  margin-right: 10px;
}
.af-card__usage {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  margin-top: 6px;
  font-size: 11px;
}
.af-card__usage-label {
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  font-size: 10px;
  letter-spacing: 0.4px;
}
.af-card__usage-text {
  color: rgba(255, 255, 255, 0.85);
  margin-right: 8px;
}

.af-card__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.af-badge {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 3px 8px;
  border-radius: 10px;
  white-space: nowrap;
}
.af-badge--archived {
  background: rgba(125, 125, 125, 0.18);
  color: rgba(255, 255, 255, 0.6);
}
</style>

<style>
/* Light mode parity for the af-card language, matching the white-content
   overrides the passes list uses. */
.white-content .af-card {
  background: rgba(128, 128, 128, 0.08);
}
.white-content .af-card:hover {
  background: rgba(128, 128, 128, 0.16);
}
.white-content .af-card__meta,
.white-content .af-empty-state__hint {
  color: rgba(60, 60, 80, 0.75);
}
.white-content .af-card__usage-label {
  color: rgba(60, 60, 80, 0.5);
}
.white-content .af-card__usage-text {
  color: rgba(60, 60, 80, 0.85);
}
.white-content .af-badge--archived {
  color: rgba(60, 60, 80, 0.6);
}
</style>
